import { useState } from "react";
import Card from "../../components/common/Card";
import Button from "../../components/common/Button";
import Input from "../../components/common/Input";
import Checkbox from "../../components/common/Checkbox";
import Alert from "../../components/common/Alert";
import Modal from "../../components/common/Modal";
import PageHeader from "../../components/common/PageHeader";
import Table from "../../components/common/Table";
import ActionButtons from "../../components/common/ActionButtons";
import {
  useGetTaxRatesQuery,
  useCreateTaxRateMutation,
  useUpdateTaxRateMutation,
  useDeleteTaxRateMutation,
} from "../../store/api";

const TaxRates = () => {
  const { data, isLoading, isError, error } = useGetTaxRatesQuery();
  const [createTaxRate, createState] = useCreateTaxRateMutation();
  const [updateTaxRate, updateState] = useUpdateTaxRateMutation();
  const [deleteTaxRate] = useDeleteTaxRateMutation();

  const [form, setForm] = useState({
    name: "",
    ratePercent: "",
    isActive: true,
  });
  const [editingId, setEditingId] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [deleteModal, setDeleteModal] = useState({
    isOpen: false,
    taxRateId: null,
    taxRateName: "",
  });
  const [errorModal, setErrorModal] = useState({ isOpen: false, message: "" });

  const taxRates = Array.isArray(data) ? data : [];

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setForm((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        name: form.name,
        ratePercent: parseFloat(form.ratePercent),
        isActive: form.isActive,
      };
      if (editingId) {
        await updateTaxRate({
          taxRateId: editingId,
          payload,
        }).unwrap();
        setEditingId(null);
      } else {
        await createTaxRate(payload).unwrap();
      }
      setForm({ name: "", ratePercent: "", isActive: true });
      setShowForm(false);
    } catch (err) {
      // error handled by UI
    }
  };

  const handleEdit = (taxRate) => {
    setEditingId(taxRate.taxRateId);
    setForm({
      name: taxRate.name || "",
      ratePercent: taxRate.ratePercent || "",
      isActive: taxRate.isActive !== undefined ? taxRate.isActive : true,
    });
    setShowForm(true);
  };

  const handleDelete = (taxRateId) => {
    const taxRate = taxRates.find((t) => t.taxRateId === taxRateId);
    setDeleteModal({
      isOpen: true,
      taxRateId,
      taxRateName: taxRate?.name || "this tax rate",
    });
  };

  const confirmDelete = async () => {
    try {
      await deleteTaxRate(deleteModal.taxRateId).unwrap();
      setDeleteModal({ isOpen: false, taxRateId: null, taxRateName: "" });
    } catch (err) {
      setDeleteModal({ isOpen: false, taxRateId: null, taxRateName: "" });
      const errorMessage =
        err?.data?.detail ||
        err?.data?.message ||
        "Failed to delete tax rate. It may be in use.";
      setErrorModal({ isOpen: true, message: errorMessage });
    }
  };

  const handleCancel = () => {
    setEditingId(null);
    setForm({ name: "", ratePercent: "", isActive: true });
    setShowForm(false);
  };

  const columns = [
    { key: "name", label: "Tax Name" },
    { key: "rate", label: "Rate (%)" },
    { key: "status", label: "Status" },
    { key: "actions", label: "Actions" },
  ];

  const rows = taxRates.map((taxRate) => {
    return {
      id: taxRate.taxRateId,
      name: taxRate.name,
      rate: `${parseFloat(taxRate.ratePercent || 0).toFixed(2)}%`,
      status: (
        <span className="text-sm text-slate-700 dark:text-slate-300">
          {taxRate.isActive ? "Active" : "Inactive"}
        </span>
      ),
      actions: (
        <div className="flex gap-2">
          <button
            type="button"
            className="p-1 text-[#4f46e5] dark:text-indigo-400 hover:bg-indigo-50 dark:hover:bg-indigo-900/30 rounded transition"
            onClick={() => handleEdit(taxRate)}
            title="Edit"
            aria-label="Edit tax rate"
          >
            <svg
              className="h-5 w-5"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"
              />
            </svg>
          </button>
          <button
            type="button"
            className="p-1 text-rose-600 dark:text-rose-400 hover:bg-rose-50 dark:hover:bg-rose-900/30 rounded transition"
            onClick={() => handleDelete(taxRate.taxRateId)}
            title="Delete"
            aria-label="Delete tax rate"
          >
            <svg
              className="h-5 w-5"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
              />
            </svg>
          </button>
        </div>
      ),
    };
  });

  return (
    <div className="space-y-6">
      <PageHeader
        title="Tax Rates"
        subtitle="Manage tax rates for your products."
        actions={
          showForm ? (
            <Button size="sm" variant="secondary" onClick={handleCancel}>
              Back
            </Button>
          ) : (
            <Button size="sm" onClick={() => setShowForm(true)}>
              Add Tax Rate
            </Button>
          )
        }
      />

      {showForm && (
        <Card>
          <h2 className="mb-4 text-lg font-semibold text-slate-900 dark:text-slate-100">
            {editingId ? "Edit Tax Rate" : "Add Tax Rate"}
          </h2>
          <form className="space-y-4" onSubmit={handleSubmit}>
            <div className="grid gap-4 md:grid-cols-2">
              <Input
                label="Tax Name"
                name="name"
                placeholder="GST"
                value={form.name}
                onChange={handleChange}
                required
              />
              <Input
                label="Rate (%)"
                name="ratePercent"
                type="number"
                step="0.01"
                placeholder="5.00"
                value={form.ratePercent}
                onChange={handleChange}
                required
                helper="Enter percentage (e.g., 5.00 for 5%)"
              />
            </div>

            <Checkbox
              label="Active"
              name="isActive"
              checked={form.isActive}
              onChange={handleChange}
            />

            {(createState.isSuccess || updateState.isSuccess) && (
              <Alert tone="success">
                Tax rate {editingId ? "updated" : "created"} successfully.
              </Alert>
            )}
            {(createState.isError || updateState.isError) && (
              <Alert tone="error">
                {createState.error?.data?.detail ||
                  updateState.error?.data?.detail ||
                  "Failed to save tax rate."}
              </Alert>
            )}

            <div className="flex gap-3">
              <Button
                type="submit"
                disabled={createState.isLoading || updateState.isLoading}
              >
                {editingId ? "Update" : "Add"} Tax Rate
              </Button>
              <Button type="button" variant="secondary" onClick={handleCancel}>
                Cancel
              </Button>
            </div>
          </form>
        </Card>
      )}

      {!showForm && (
        <Card className="p-0">
          <div className="border-b border-slate-100 dark:border-slate-700 px-6 py-4 text-sm font-semibold text-slate-700 dark:text-slate-300">
            All Tax Rates
          </div>
          <div className="px-6 py-4">
            {isLoading && (
              <p className="text-sm text-slate-500 dark:text-slate-400">
                Loading tax rates...
              </p>
            )}
            {isError && (
              <Alert tone="error">
                {error?.data?.detail || "Failed to load tax rates."}
              </Alert>
            )}
            {!isLoading && !isError && (
              <Table
                columns={columns}
                rows={rows}
                emptyText="No tax rates yet."
              />
            )}
          </div>
        </Card>
      )}

      {/* Delete Confirmation Modal */}
      <Modal
        isOpen={deleteModal.isOpen}
        onClose={() =>
          setDeleteModal({ isOpen: false, taxRateId: null, taxRateName: "" })
        }
        title="Delete Tax Rate"
        confirmText="Delete"
        confirmVariant="primary"
        onConfirm={confirmDelete}
      >
        <p className="text-slate-900 dark:text-slate-100">
          Are you sure you want to delete{" "}
          <strong>{deleteModal.taxRateName}</strong>?
        </p>
        <p className="mt-2 text-sm text-slate-600 dark:text-slate-400">
          This action cannot be undone. The tax rate cannot be deleted if it's
          in use.
        </p>
      </Modal>

      {/* Error Modal */}
      <Modal
        isOpen={errorModal.isOpen}
        onClose={() => setErrorModal({ isOpen: false, message: "" })}
        title="Error"
        confirmText="OK"
        onConfirm={() => setErrorModal({ isOpen: false, message: "" })}
      >
        <p className="text-slate-900 dark:text-slate-100">
          {errorModal.message}
        </p>
      </Modal>
    </div>
  );
};

export default TaxRates;
